import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button, Badge } from '../components/ui';
import { 
  Sparkles, 
  Camera, 
  Calendar, 
  MapPin, 
  Users, 
  Shield,
  ChevronRight,
  Star,
  Check
} from 'lucide-react';

const Landing = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handlePricingClick = async (planIndex) => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (planIndex === 0) {
      navigate('/dashboard');
      return;
    }
    const packageId = planIndex === 1 ? 'monthly' : 'yearly';
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin,
          lang: document.documentElement.lang || 'tr'
        })
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert(data.detail || 'Ödeme başlatılamadı');
      }
    } catch (e) {
      alert('Ödeme servisi bağlantı hatası');
    }
  };

  const features = [
    {
      icon: Camera,
      title: t('landing.feat1Title', 'Photo Archive'),
      description: t('landing.feat1Desc', 'Track and compare your tattoo healing progress with photos.')
    },
    {
      icon: Calendar,
      title: t('landing.feat2Title', 'Care Reminders'),
      description: t('landing.feat2Desc', 'Never forget your moisturizing, sun protection and care routines.')
    },
    {
      icon: MapPin,
      title: t('landing.feat3Title', 'Body Map'),
      description: t('landing.feat3Desc', 'Plan your future tattoos on an interactive body map.')
    },
    {
      icon: Users,
      title: t('landing.feat4Title', 'Artist Guide'),
      description: t('landing.feat4Desc', 'Find the best tattoo artists, review them and explore portfolios.')
    },
    {
      icon: Sparkles,
      title: t('landing.feat5Title', 'AI Color Analysis'),
      description: t('landing.feat5Desc', 'Analyze your tattoo color fading with artificial intelligence.')
    },
    {
      icon: Shield,
      title: t('landing.feat6Title', 'Care Guide'),
      description: t('landing.feat6Desc', 'Protect your tattoos with expert care protocols.')
    },
  ];

  const pricing = [
    {
      name: t('landing.pricing.free.name', 'Free'),
      price: '0',
      features: t('landing.pricing.free.features', { returnObjects: true, defaultValue: ['3 tattoo records', 'Basic care reminders', 'Care guide'] }),
      cta: t('nav.getStarted', 'Get Started'),
      popular: false
    },
    {
      name: t('landing.pricing.monthly.name', 'Monthly'),
      price: '4.99',
      features: t('landing.pricing.monthly.features', { returnObjects: true, defaultValue: ['Unlimited tattoos', 'AI Smart Check', 'AR Preview', 'Priority support'] }),
      cta: t('landing.goPremium', 'Go Premium'),
      popular: true
    },
    {
      name: t('landing.pricing.yearly.name', 'Yearly'),
      price: '39.99',
      period: t('landing.pricing.yearly.period', '/year'),
      features: t('landing.pricing.yearly.features', { returnObjects: true, defaultValue: ['All premium features', 'AR tattoo preview', '2 months free', 'Priority support'] }),
      cta: t('landing.bestPrice', 'Best Price'),
      popular: false
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1671245811619-a482d0207041?w=1920')` 
          }}
        />
        <div className="absolute inset-0 hero-glow" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background" />

        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Badge className="mb-6" data-testid="hero-badge">{t('landing.badge', 'For Tattoo Lovers')}</Badge>
            <h1 className="font-heading text-5xl md:text-7xl lg:text-8xl mb-6 leading-tight">
              {t('landing.heroLine1', 'PROTECT YOUR')}
              <span className="block text-primary">{t('landing.heroLine2', 'INK')}</span>
              {t('landing.heroLine3', 'ART')}
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
              {t('landing.heroDesc', 'Track your tattoo care, monitor healing progress, plan future designs and discover the best artists.')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to={user ? '/dashboard' : '/login'}>
                <Button size="lg" className="w-full sm:w-auto" data-testid="hero-cta-btn">
                  {user ? t('landing.goDashboard', 'Go to Dashboard') : t('landing.startFree', 'Start Free')}
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </Link>
              <a href="#features">
                <Button variant="outline" size="lg" className="w-full sm:w-auto" data-testid="features-btn">
                  {t('landing.exploreFeatures', 'Explore Features')}
                </Button>
              </a>
            </div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-6 h-10 border-2 border-muted-foreground rounded-full flex justify-center">
            <div className="w-1.5 h-3 bg-primary rounded-full mt-2" />
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl mb-4">
              {t('landing.featuresTitle1', 'ALL YOUR NEEDS')} <span className="text-primary">{t('landing.featuresTitle2', 'IN ONE PLATFORM')}</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              {t('landing.featuresDesc', 'Everything from tattoo care to finding artists, all in one place.')}
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="card p-8 hover:border-primary/50 transition-all duration-300 group"
                  data-testid={`feature-card-${index}`}
                >
                  <div className="w-14 h-14 bg-primary/10 rounded-sm flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                    <Icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-heading text-xl mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Showcase Section */}
      <section className="py-24 px-4 bg-background-secondary">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-4xl md:text-5xl mb-6">
                {t('landing.showcaseTitle', 'TRACK YOUR')} <span className="text-primary">{t('landing.showcaseHighlight', 'TATTOO JOURNEY')}</span>
              </h2>
              <p className="text-muted-foreground text-lg mb-8">
                {t('landing.showcaseDesc', 'Every tattoo has a story. Record that story with InkTrack - document the healing process, care routines, and changes over time from day one.')}
              </p>
              <ul className="space-y-4 mb-8">
                {t('landing.showcaseFeatures', { returnObjects: true, defaultValue: ['Photo comparison', 'Healing timeline', 'AI-powered analysis'] }).map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-primary/20 rounded-sm flex items-center justify-center">
                      <Check className="w-4 h-4 text-primary" />
                    </div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <Link to={user ? '/collection' : '/login'}>
                <Button data-testid="showcase-cta">
                  {t('landing.showcaseCta', 'Start Your Collection')}
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src="https://images.unsplash.com/photo-1640202430303-a71359ade259?w=400" 
                  alt="Tattoo" 
                  className="rounded-sm w-full h-48 object-cover"
                />
                <img 
                  src="https://images.unsplash.com/photo-1668873882473-d92da2c31a96?w=400" 
                  alt="Tattoo" 
                  className="rounded-sm w-full h-48 object-cover mt-8"
                />
                <img 
                  src="https://images.pexels.com/photos/3511738/pexels-photo-3511738.jpeg?w=400" 
                  alt="Artist" 
                  className="rounded-sm w-full h-48 object-cover -mt-4"
                />
                <img 
                  src="https://images.pexels.com/photos/3327157/pexels-photo-3327157.jpeg?w=400" 
                  alt="Tattoo" 
                  className="rounded-sm w-full h-48 object-cover"
                />
              </div>
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-secondary/20 blur-3xl -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl mb-4">
              PLANLAR VE <span className="text-primary">FİYATLAR</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              İhtiyaçlarınıza uygun planı seçin
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {pricing.map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`card p-8 relative ${plan.popular ? 'border-primary' : ''}`}
                data-testid={`pricing-card-${index}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge>En Popüler</Badge>
                  </div>
                )}
                <h3 className="font-heading text-2xl mb-2">{plan.name}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-heading text-primary">${plan.price}</span>
                  <span className="text-muted-foreground">{plan.period || '/ay'}</span>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  variant={plan.popular ? 'default' : 'outline'}
                  className="w-full"
                  data-testid={`pricing-cta-${index}`}
                  onClick={() => handlePricingClick(index)}
                >
                  {plan.cta}
                </Button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 relative overflow-hidden">
        <div className="absolute inset-0 hero-glow opacity-50" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-3xl mx-auto text-center relative z-10"
        >
          <h2 className="font-heading text-4xl md:text-5xl mb-6">
            {t('landing.ctaTitle', 'START YOUR TATTOO JOURNEY')} <span className="text-primary">{t('landing.ctaHighlight', 'TODAY')}</span>
          </h2>
          <p className="text-muted-foreground text-lg mb-10">
            {t('landing.ctaDesc', 'Thousands of tattoo lovers manage their collections with InkTrack. Join them.')}
          </p>
          <Link to={user ? '/dashboard' : '/login'}>
            <Button size="lg" className="animate-pulse-glow" data-testid="final-cta">
              {t('landing.ctaButton', 'Create Free Account')}
              <ChevronRight className="w-5 h-5" />
            </Button>
          </Link>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="font-heading text-2xl mb-4">
                INK<span className="text-primary">TRACK</span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Dövme sahipleri ve meraklıları için kapsamlı bakım ve koleksiyon yönetim platformu.
              </p>
              <p className="text-xs text-muted-foreground">
                Powered by CerebrAI-VorTX Neurotechnology
              </p>
            </div>
            
            {/* Links */}
            <div>
              <h4 className="font-heading text-sm mb-4">BAĞLANTILAR</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="#features" className="hover:text-white transition-colors">Özellikler</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Fiyatlar</a></li>
                <li><Link to="/smart-check" className="hover:text-white transition-colors">Smart Check</Link></li>
                <li><Link to="/ar-preview" className="hover:text-white transition-colors">AR Önizleme</Link></li>
              </ul>
            </div>
            
            {/* Legal */}
            <div>
              <h4 className="font-heading text-sm mb-4">{t('landing.footer.legal', 'YASAL')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><Link to="/privacy" className="hover:text-white transition-colors">{t('landing.footer.privacy', 'Gizlilik Politikasi')}</Link></li>
                <li><Link to="/terms" className="hover:text-white transition-colors">{t('landing.footer.terms', 'Kullanim Sartlari')}</Link></li>
                <li><Link to="/distance-sales" className="hover:text-white transition-colors">{t('landing.footer.distanceSales', 'Mesafeli Satis Sozlesmesi')}</Link></li>
                <li><Link to="/delivery-return" className="hover:text-white transition-colors">{t('landing.footer.deliveryReturn', 'Teslimat ve Iade')}</Link></li>
                <li><Link to="/privacy#kvkk" className="hover:text-white transition-colors">{t('landing.footer.kvkk', 'KVKK Aydinlatma')}</Link></li>
                <li><Link to="/about" className="hover:text-white transition-colors">{t('landing.footer.about', 'Hakkimizda')}</Link></li>
                <li><a href="mailto:info@ink-track.com" className="hover:text-white transition-colors">{t('landing.footer.contact', 'Iletisim')}</a></li>
              </ul>
            </div>
          </div>

          {/* Payment Logos */}
          <div className="flex items-center justify-center gap-6 py-6 border-t border-border">
            <img src="/images/iyzico-footer.svg" alt="iyzico ile güvenli ödeme" className="h-8 opacity-80 hover:opacity-100 transition-opacity" />
            <div className="flex items-center gap-2">
              <svg className="h-7 w-11" viewBox="0 0 48 32" fill="none"><rect width="48" height="32" rx="4" fill="#1A1F71"/><path d="M18.5 21.5h-3l1.9-11h3l-1.9 11zm12.4-10.7c-.6-.2-1.5-.5-2.7-.5-3 0-5.1 1.5-5.1 3.7 0 1.6 1.5 2.5 2.6 3 1.1.6 1.5.9 1.5 1.4 0 .8-.9 1.1-1.7 1.1-1.1 0-1.8-.2-2.7-.6l-.4-.2-.4 2.4c.7.3 1.9.6 3.2.6 3.2 0 5.2-1.5 5.2-3.8 0-1.3-.8-2.2-2.5-3-.9-.5-1.5-.9-1.5-1.4 0-.5.5-1 1.5-1 .9 0 1.5.2 2 .4l.2.1.4-2.2zm7.9-.3h-2.3c-.7 0-1.3.2-1.6.9l-4.4 10.1h3.2l.6-1.7h3.8l.4 1.7h2.8l-2.5-11zm-3.6 7.1l1.2-3.1.5-1.3.3 1.3.7 3.1h-2.7zM16.3 10.5l-2.8 7.5-.3-1.5c-.5-1.7-2.1-3.5-3.9-4.4l2.7 9.9h3.2l4.8-11.5h-3.7z" fill="white"/><path d="M10.5 10.5H5.6l-.1.3c3.8.9 6.3 3.2 7.4 5.9l-1.1-5.3c-.2-.7-.7-.9-1.3-.9z" fill="#F9A825"/></svg>
              <svg className="h-7 w-11" viewBox="0 0 48 32" fill="none"><rect width="48" height="32" rx="4" fill="#252525"/><circle cx="19" cy="16" r="8" fill="#EB001B"/><circle cx="29" cy="16" r="8" fill="#F79E1B"/><path d="M24 10.3a8 8 0 0 1 0 11.4 8 8 0 0 1 0-11.4z" fill="#FF5F00"/></svg>
            </div>
            <span className="text-xs text-muted-foreground">{t('landing.footer.securePayment', 'Guvenli Odeme')}</span>
          </div>
          
          {/* Bottom */}
          <div className="border-t border-border pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_perfect-app-maker-1/artifacts/cqpwi736_premium%20%282%29.png" 
                  alt="CerebrAI-VorTX" 
                  className="h-12 w-auto"
                />
                <div className="text-xs text-muted-foreground text-left">
                  <p className="font-medium mb-1">CEREBRAI VORTX NÖROTEKNOLOJİ VE YAZILIM SİSTEMLERİ LTD. ŞTİ.</p>
                  <p>Ulubağ Mah. Recep Tayyip Erdoğan Blv. Harran Üniversitesi Teknokent No: 287 A</p>
                  <p>İç Kapı No: Z1 Haliliye / Şanlıurfa, Türkiye</p>
                </div>
              </div>
              <div className="text-sm text-muted-foreground">
                © 2026 InkTrack. {t('landing.footer.rights', 'Tum haklari saklidir.')}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
